/**
 * The in-house image-annotation model + render layer (ADR-025 A3a). Legacy `markerjs2` parity WITHOUT the dependency /
 * watermark (the legacy shipped a source-patched copy to strip the logo; markerjs3 re-adds an output watermark — see the ADR),
 * so we own the six tools on a plain `<canvas>`. Shapes are stored in the image's NATURAL pixel coordinates (the editor canvas
 * is displayed scaled, but the drawing buffer + export are natural-size — legacy `renderAtNaturalSize`). Rendering is a Strategy
 * per shape type ({@link drawAnnotation}); interaction lives in the editor component.
 */

/** The drawing tools plus `select` — the pointer mode that hit-tests + moves/deletes an already-placed shape (not a shape type). */
export type Tool = "select" | "rect" | "ellipse" | "arrow" | "freehand" | "text" | "callout";

/** The legacy palette (`ARLMS_ModalWindows.js`): red default, then yellow/blue/white. */
export const PALETTE = ["#e11d0c", "#f4c20d", "#2563eb", "#ffffff"] as const;
export type AnnoColor = string;

export interface Point {
  x: number;
  y: number;
}
interface Base {
  id: string;
  color: AnnoColor;
}
export interface RectAnno extends Base {
  type: "rect";
  x: number;
  y: number;
  w: number;
  h: number;
  /** Draw a semi-transparent colour fill inside the outline (a highlighted region), not just the frame. */
  fill?: boolean;
}
export interface EllipseAnno extends Base {
  type: "ellipse";
  x: number;
  y: number;
  w: number;
  h: number;
  /** Draw a semi-transparent colour fill inside the outline (a highlighted region), not just the frame. */
  fill?: boolean;
}
export interface ArrowAnno extends Base {
  type: "arrow";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}
export interface FreehandAnno extends Base {
  type: "freehand";
  points: Point[];
}
export interface TextAnno extends Base {
  type: "text";
  x: number;
  y: number;
  text: string;
  fontSize: number;
}
export interface CalloutAnno extends Base {
  type: "callout";
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
  fontSize: number;
}
export type Annotation = RectAnno | EllipseAnno | ArrowAnno | FreehandAnno | TextAnno | CalloutAnno;

/** Stroke width + base font size derived from the image so markup reads at any resolution (legacy scaled to natural size). */
export function strokeWidthFor(imageW: number, imageH: number): number {
  return Math.max(2, Math.round(Math.min(imageW, imageH) / 200));
}
export function fontSizeFor(imageW: number, imageH: number): number {
  return Math.max(14, Math.round(Math.min(imageW, imageH) / 28));
}

/** A short unique-enough id for a new shape (Date/Math.random are fine on the client). */
export function newId(): string {
  return `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

/** Normalize a drag rectangle so w/h are positive regardless of drag direction. */
export function normRect(x0: number, y0: number, x1: number, y1: number): { x: number; y: number; w: number; h: number } {
  return { x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) };
}

/** A contrasting outline (dark for light strokes, light for dark) so any color reads on any background. */
function contrastInk(color: AnnoColor): string {
  return color.toLowerCase() === "#ffffff" || color.toLowerCase() === "#fff" ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.85)";
}

/** Render ONE annotation onto the natural-size canvas context. `lw` = stroke width, `fs` = base font size. */
export function drawAnnotation(ctx: CanvasRenderingContext2D, a: Annotation, lw: number, fs: number): void {
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = a.color;
  ctx.fillStyle = a.color;
  ctx.lineWidth = lw;

  switch (a.type) {
    case "rect":
      if (a.fill) {
        ctx.globalAlpha = 0.3; // a translucent highlight under the full-opacity outline
        ctx.fillRect(a.x, a.y, a.w, a.h);
        ctx.globalAlpha = 1;
      }
      ctx.strokeRect(a.x, a.y, a.w, a.h);
      break;
    case "ellipse": {
      ctx.beginPath();
      ctx.ellipse(a.x + a.w / 2, a.y + a.h / 2, Math.abs(a.w / 2), Math.abs(a.h / 2), 0, 0, Math.PI * 2);
      if (a.fill) {
        ctx.globalAlpha = 0.3;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      ctx.stroke();
      break;
    }
    case "arrow":
      drawArrow(ctx, a, lw);
      break;
    case "freehand": {
      if (a.points.length < 2) break;
      ctx.beginPath();
      ctx.moveTo(a.points[0]!.x, a.points[0]!.y);
      for (let i = 1; i < a.points.length; i++) ctx.lineTo(a.points[i]!.x, a.points[i]!.y);
      ctx.stroke();
      break;
    }
    case "text":
      drawText(ctx, a);
      break;
    case "callout":
      drawCallout(ctx, a, lw, fs);
      break;
  }
  ctx.restore();
}

function drawArrow(ctx: CanvasRenderingContext2D, a: ArrowAnno, lw: number): void {
  ctx.beginPath();
  ctx.moveTo(a.x1, a.y1);
  ctx.lineTo(a.x2, a.y2);
  ctx.stroke();
  // Arrowhead — two barbs at the tip, sized off the stroke width.
  const angle = Math.atan2(a.y2 - a.y1, a.x2 - a.x1);
  const head = Math.max(12, lw * 5);
  const spread = Math.PI / 7;
  ctx.beginPath();
  ctx.moveTo(a.x2, a.y2);
  ctx.lineTo(a.x2 - head * Math.cos(angle - spread), a.y2 - head * Math.sin(angle - spread));
  ctx.moveTo(a.x2, a.y2);
  ctx.lineTo(a.x2 - head * Math.cos(angle + spread), a.y2 - head * Math.sin(angle + spread));
  ctx.stroke();
}

function drawText(ctx: CanvasRenderingContext2D, a: TextAnno): void {
  ctx.font = `600 ${a.fontSize}px Helvetica, Arial, sans-serif`;
  ctx.textBaseline = "top";
  const lines = a.text.split("\n");
  // A contrasting halo so the label reads on any image; then the colored fill on top.
  ctx.lineWidth = Math.max(2, a.fontSize / 8);
  ctx.strokeStyle = contrastInk(a.color);
  ctx.fillStyle = a.color;
  lines.forEach((line, i) => {
    const y = a.y + i * a.fontSize * 1.25;
    ctx.strokeText(line, a.x, y);
    ctx.fillText(line, a.x, y);
  });
}

function drawCallout(ctx: CanvasRenderingContext2D, a: CalloutAnno, lw: number, fs: number): void {
  const r = Math.min(12, a.h / 3);
  // Rounded box (white fill, colored border) — the legacy Takeda callout.
  ctx.beginPath();
  roundRect(ctx, a.x, a.y, a.w, a.h, r);
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.fill();
  ctx.lineWidth = lw;
  ctx.strokeStyle = a.color;
  ctx.stroke();
  // Tail — a small triangle from the bottom-left toward the lower-left.
  ctx.beginPath();
  ctx.moveTo(a.x + a.w * 0.2, a.y + a.h);
  ctx.lineTo(a.x + a.w * 0.35, a.y + a.h);
  ctx.lineTo(a.x + a.w * 0.12, a.y + a.h + Math.max(16, a.h * 0.45));
  ctx.closePath();
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.fill();
  ctx.stroke();
  // Text inside — dark for contrast on the white box.
  ctx.font = `600 ${a.fontSize}px Helvetica, Arial, sans-serif`;
  ctx.textBaseline = "top";
  ctx.fillStyle = "#111827";
  const pad = Math.max(6, fs / 3);
  a.text.split("\n").forEach((line, i) => ctx.fillText(line, a.x + pad, a.y + pad + i * a.fontSize * 1.25));
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ── Selection / move (Select tool) — all in NATURAL pixels ────────────────────────────────────────────────────────────

/**
 * The axis-aligned bounding box of an annotation, in natural pixels. Text is measured with the EXACT font `drawText` uses
 * (weight/size/family), so the box matches what's painted. Used for the selection ring and for filled-shape hit-testing.
 */
export function annoBounds(a: Annotation, ctx: CanvasRenderingContext2D): { x: number; y: number; w: number; h: number } {
  switch (a.type) {
    case "rect":
    case "ellipse":
    case "callout":
      return normRect(a.x, a.y, a.x + a.w, a.y + a.h);
    case "arrow":
      return normRect(a.x1, a.y1, a.x2, a.y2);
    case "freehand": {
      const xs = a.points.map((p) => p.x);
      const ys = a.points.map((p) => p.y);
      const x = Math.min(...xs);
      const y = Math.min(...ys);
      return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
    }
    case "text": {
      ctx.font = `600 ${a.fontSize}px Helvetica, Arial, sans-serif`;
      const lines = a.text.split("\n");
      const w = Math.max(0, ...lines.map((l) => ctx.measureText(l).width));
      return { x: a.x, y: a.y, w, h: lines.length * a.fontSize * 1.25 };
    }
  }
}

/** Distance from point `p` to the segment a→b (0 if the segment is a point). */
function distToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Is the point within (a slop of) this annotation? Filled shapes = bounding box; thin shapes (arrow/freehand) = line distance. */
export function isPointInAnnotation(a: Annotation, p: Point, ctx: CanvasRenderingContext2D, slop: number): boolean {
  if (a.type === "arrow") return distToSegment(p, { x: a.x1, y: a.y1 }, { x: a.x2, y: a.y2 }) <= slop;
  if (a.type === "freehand") {
    if (a.points.length === 1) return Math.hypot(p.x - a.points[0]!.x, p.y - a.points[0]!.y) <= slop;
    for (let i = 1; i < a.points.length; i++) if (distToSegment(p, a.points[i - 1]!, a.points[i]!) <= slop) return true;
    return false;
  }
  const b = annoBounds(a, ctx);
  return p.x >= b.x - slop && p.x <= b.x + b.w + slop && p.y >= b.y - slop && p.y <= b.y + b.h + slop;
}

/** The top-most (last-drawn) annotation under the point, or null — so clicking overlapping shapes grabs the visible one. */
export function hitTest(shapes: Annotation[], p: Point, ctx: CanvasRenderingContext2D, slop: number): Annotation | null {
  for (let i = shapes.length - 1; i >= 0; i--) if (isPointInAnnotation(shapes[i]!, p, ctx, slop)) return shapes[i]!;
  return null;
}

/**
 * Return a NEW annotation shifted by (dx,dy). NEVER mutates the input: the undo history (`past`/`future`) shares annotation
 * object references, so an in-place shift would silently corrupt earlier snapshots (panel: history aliasing).
 */
export function translateAnnotation(a: Annotation, dx: number, dy: number): Annotation {
  switch (a.type) {
    case "arrow":
      return { ...a, x1: a.x1 + dx, y1: a.y1 + dy, x2: a.x2 + dx, y2: a.y2 + dy };
    case "freehand":
      return { ...a, points: a.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
    default:
      return { ...a, x: a.x + dx, y: a.y + dy };
  }
}

/**
 * Draw the editor-only selection affordance — a dashed ring around the shape's bounds. This is CHROME, NOT artwork: it is
 * drawn in a separate pass after {@link drawAnnotation} and MUST be suppressed before the export `toBlob`, or it burns into
 * the saved PNG (panel). Widths/dashes scale off the image-derived stroke width so it reads at any resolution.
 */
export function drawSelectionRing(ctx: CanvasRenderingContext2D, a: Annotation, lw: number): void {
  const b = annoBounds(a, ctx);
  const pad = Math.max(4, lw * 1.5);
  ctx.save();
  ctx.setLineDash([lw * 2, lw * 1.5]);
  ctx.lineWidth = Math.max(2, Math.round(lw * 0.75));
  ctx.strokeStyle = "#2563eb"; // palette blue; dashed so it never reads as a drawn rectangle
  ctx.strokeRect(b.x - pad, b.y - pad, b.w + pad * 2, b.h + pad * 2);
  ctx.restore();
}
